import { useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import { ASSETS, CONTACT } from '../constants/site';

const LINES = ['WEB SYSTEMS', 'THAT TURN TRAFFIC', 'INTO REVENUE.'];

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: '115%' }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" data-testid="hero-section" className="circuit-bg relative min-h-screen flex items-center pt-28 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid lg:grid-cols-12 gap-14 items-center w-full">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="inline-flex items-center gap-2 border border-amber-500/30 bg-amber-500/5 rounded-full px-4 py-1.5 mb-8"
          >
            <Sparkles size={14} className="text-amber-400" />
            <span className="font-mono-tech text-[11px] tracking-[0.25em] text-amber-300/90 uppercase">
              Georgia Tech M.S. — Computer Engineering
            </span>
          </motion.div>

          <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-[4.4rem] leading-[1.04]">
            <MaskedLine delay={0.35}>{LINES[0]}</MaskedLine>
            <MaskedLine delay={0.5}>{LINES[1]}</MaskedLine>
            <MaskedLine delay={0.65}>
              <span className="text-gradient-gold">{LINES[2]}</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
          >
            GSLAB builds custom-coded websites that convert local visitors into phone calls,
            bookings and quote requests — then automates the admin work behind them.
            Rock-solid, secure, crash-proof. No templates. No plugin roulette.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#pricing"
              data-testid="hero-pricing-cta"
              className="group inline-flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] via-[#F3C64F] to-[#D4AF37] text-[#050B14] font-bold rounded-full px-7 py-3.5 hover:shadow-[0_0_36px_rgba(212,175,55,0.45)] transition-shadow duration-300"
            >
              View Pricing & Build Models
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#automations"
              data-testid="hero-automations-cta"
              className="inline-flex items-center gap-2 border border-slate-600/70 hover:border-amber-500/60 text-slate-200 rounded-full px-7 py-3.5 transition-colors duration-300"
            >
              Explore Automations
            </a>
            <a
              href={CONTACT.portfolio}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-portfolio-link"
              className="text-sm text-slate-400 hover:text-amber-300 underline-offset-4 hover:underline transition-colors"
            >
              Founder portfolio ↗
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.35 }}
            className="mt-12 grid grid-cols-3 gap-6 max-w-lg"
          >
            {[
              ['99.99%', 'EDGE UPTIME'],
              ['<2s', 'MOBILE LOAD TARGET'],
              ['5+ hrs', 'SAVED WEEKLY'],
            ].map(([v, l]) => (
              <div key={l}>
                <div className="font-mono-tech text-xl sm:text-2xl font-semibold text-amber-300">{v}</div>
                <div className="font-mono-tech text-[10px] tracking-[0.2em] text-slate-500 mt-1">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 hidden lg:flex justify-center" ref={ref} onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            style={{ rotateX: rx, rotateY: ry, transformStyle: 'preserve-3d', perspective: 900 }}
            className="relative"
          >
            <div className="absolute -inset-10 rounded-full bg-amber-500/10 blur-3xl" />
            <div className="absolute -inset-24 rounded-full border border-amber-500/10" />
            <div className="absolute -inset-24 rounded-full">
              {[0, 90, 180, 270].map((deg) => (
                <span
                  key={deg}
                  className="animate-node absolute h-1.5 w-1.5 rounded-full bg-amber-400"
                  style={{
                    top: `${50 + 46 * Math.sin((deg * Math.PI) / 180)}%`,
                    left: `${50 + 46 * Math.cos((deg * Math.PI) / 180)}%`,
                    animationDelay: `${deg / 180}s`,
                  }}
                />
              ))}
            </div>
            <img
              src={ASSETS.emblem}
              alt="GSLAB emblem"
              className="relative w-[380px] rounded-[2rem] shadow-[0_40px_120px_rgba(0,0,0,0.6)]"
              style={{ transform: 'translateZ(50px)' }}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
